/**
 * WebRTC stats -> a quality label Dart can render.
 *
 * Ported from midcore-messenger-ui/src/call/media-quality-poller.ts, with the
 * thresholds kept identical so a call that reads "poor" on Meet reads "poor"
 * here.
 *
 * Why this is in TypeScript rather than Dart: `RTCPeerConnection.getStats()`
 * returns an RTCStatsReport, a Map-like of loosely-typed dictionaries whose
 * members differ per browser. Walking it from Dart through js_interop means
 * a JSObject cast per field. This does the walk once and hands Dart a flat
 * record.
 */

import type { QualityLabel, QualitySample, QualityPollerHandle, StatsApi, StatsSource } from './types.js';

/**
 * Thresholds are ordered best-first and evaluated top-down, so a connection
 * must satisfy every condition of a tier to earn it.
 */
const TIERS: ReadonlyArray<{ label: QualityLabel; maxRtt: number; maxLoss: number }> = [
  { label: 'excellent', maxRtt: 150, maxLoss: 1 },
  { label: 'good', maxRtt: 300, maxLoss: 3 },
];

/** Emit at most one callback in five when the label has not changed. */
const HEARTBEAT_EVERY = 5;

export function classify(rttMs: number, packetLossPercent: number): QualityLabel {
  // A connection that reports neither metric is unknown, not excellent. This
  // matters on the first tick, before any delta exists.
  if (rttMs < 0 && packetLossPercent < 0) return 'unknown';

  const rtt = rttMs < 0 ? 0 : rttMs;
  const loss = packetLossPercent < 0 ? 0 : packetLossPercent;

  for (const tier of TIERS) {
    if (rtt <= tier.maxRtt && loss <= tier.maxLoss) return tier.label;
  }
  return 'poor';
}

interface Cumulative {
  packetsReceived: number;
  packetsLost: number;
  bytesReceived: number;
  bytesSent: number;
  at: number;
}

function readCumulative(report: RTCStatsReport): { cum: Cumulative; rttMs: number } {
  let packetsReceived = 0;
  let packetsLost = 0;
  let bytesReceived = 0;
  let bytesSent = 0;
  let rttMs = -1;

  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const type = s.type as string | undefined;

    if (type === 'inbound-rtp') {
      packetsReceived += Number(s.packetsReceived ?? 0);
      packetsLost += Number(s.packetsLost ?? 0);
      bytesReceived += Number(s.bytesReceived ?? 0);
    } else if (type === 'outbound-rtp') {
      bytesSent += Number(s.bytesSent ?? 0);
    } else if (type === 'candidate-pair') {
      // Only the pair actually carrying media has a meaningful RTT. Chrome
      // reports several pairs, most of them stale.
      const nominated = s.nominated === true || s.state === 'succeeded';
      const rtt = s.currentRoundTripTime;
      if (nominated && typeof rtt === 'number') {
        rttMs = Math.round(rtt * 1000);
      }
    } else if (type === 'remote-inbound-rtp' && rttMs < 0) {
      // Fallback: the remote's view of our stream carries an RTT too, and
      // Firefox populates this when it does not populate candidate-pair.
      const rtt = s.roundTripTime;
      if (typeof rtt === 'number') rttMs = Math.round(rtt * 1000);
    }
  });

  return {
    cum: { packetsReceived, packetsLost, bytesReceived, bytesSent, at: Date.now() },
    rttMs,
  };
}

/**
 * Turn two cumulative readings into a rate. Returns -1 rather than 0 when
 * there is no previous reading, because "no data yet" and "zero throughput"
 * are different things and the UI shows them differently.
 */
function delta(
  prev: Cumulative | null,
  next: Cumulative
): {
  lossPercent: number;
  inboundKbps: number;
  outboundKbps: number;
} {
  if (!prev) return { lossPercent: -1, inboundKbps: -1, outboundKbps: -1 };

  const seconds = Math.max(0.001, (next.at - prev.at) / 1000);
  const recv = next.packetsReceived - prev.packetsReceived;
  const lost = next.packetsLost - prev.packetsLost;
  const expected = recv + lost;

  return {
    // Loss over the INTERVAL, not since the call began. A call that dropped
    // packets in its first seconds and recovered should not read "poor" for
    // the rest of its life.
    lossPercent: expected > 0 ? Math.max(0, (lost / expected) * 100) : 0,
    inboundKbps: Math.max(0, ((next.bytesReceived - prev.bytesReceived) * 8) / 1000 / seconds),
    outboundKbps: Math.max(0, ((next.bytesSent - prev.bytesSent) * 8) / 1000 / seconds),
  };
}

function toSample(prev: Cumulative | null, report: RTCStatsReport): { sample: QualitySample; cum: Cumulative } {
  const { cum, rttMs } = readCumulative(report);
  const d = delta(prev, cum);
  return {
    cum,
    sample: {
      label: classify(rttMs, d.lossPercent),
      rttMs,
      packetLossPercent: d.lossPercent < 0 ? -1 : Math.round(d.lossPercent * 10) / 10,
      inboundKbps: d.inboundKbps < 0 ? -1 : Math.round(d.inboundKbps),
      outboundKbps: d.outboundKbps < 0 ? -1 : Math.round(d.outboundKbps),
      at: cum.at,
    },
  };
}

export const stats: StatsApi = {
  async sample(source: StatsSource): Promise<QualitySample> {
    const report = await source.getStats();
    return toSample(null, report).sample;
  },

  poll(source: StatsSource, intervalMs: number, onSample: (sample: QualitySample) => void): QualityPollerHandle {
    let prev: Cumulative | null = null;
    let latest: QualitySample | null = null;
    let lastLabel: QualityLabel | null = null;
    let sinceEmit = 0;
    let stopped = false;

    const tick = async (): Promise<void> => {
      if (stopped) return;
      try {
        const report = await source.getStats();
        const { sample, cum } = toSample(prev, report);
        prev = cum;
        latest = sample;

        sinceEmit++;
        // Emit on a change, or periodically so a steady connection still
        // refreshes its bitrate readout. Without this the roster would
        // re-render every second for a call that is not moving.
        if (sample.label !== lastLabel || sinceEmit >= HEARTBEAT_EVERY) {
          lastLabel = sample.label;
          sinceEmit = 0;
          onSample(sample);
        }
      } catch {
        // A closed peer connection throws. The caller stops us on teardown;
        // until then, skipping a tick is better than an unhandled rejection.
      }
    };

    const timer = setInterval(() => void tick(), Math.max(250, intervalMs));
    void tick();

    return {
      stop(): void {
        stopped = true;
        clearInterval(timer);
      },
      latest(): QualitySample | null {
        return latest;
      },
    };
  },
};
